import { useQuery } from "@tanstack/react-query";
import { RefreshCw, X } from "lucide-react";
import React from "react";

import {
  type BypassCounts,
  type BypassFilter,
  type BypassGroup,
  type BypassSignal,
  getBypassPallets,
  getBypassReport,
} from "@/api/warehouse_ops";
import { DateInput } from "@/components/DateInput";
import { Button } from "@/components/ui/button";
import { fmtDate, ruNum } from "@/lib/format";
import { BottomSheet } from "@/lib/scan-ui/overlays";
import { LoadState } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";
import {
  BYPASS_PALLET_COLUMNS,
  bypassPctText,
  defaultBypassPeriod,
  detailValue,
  monthTitle,
  noneStatesText,
  postingSyncText,
  todayMsk,
} from "@/lib/warehouse-ops";

import { ExcludedBlock } from "./ExcludedBlock";
import { RefetchError } from "./RefetchError";

/**
 * «Отгрузки мимо портала» (`GET …/bypass`): собранные в портале паллеты по
 * месяцам первой сборки — отгружены со сканом в портале, только проведением
 * документа сборки в 1С (мимо портала) или ещё без сигнала; в разрезе
 * направления, юрлица и покупателя. Клик по числу — паллеты за ним.
 */
export function BypassTab(): React.ReactElement {
  const [initFrom, initTo] = defaultBypassPeriod(todayMsk());
  const [from, setFrom] = React.useState(initFrom);
  const [to, setTo] = React.useState(initTo);
  const [drill, setDrill] = React.useState<BypassFilter | null>(null);
  const periodOk = !!from && !!to && from <= to;
  const q = useQuery({
    queryKey: ["warehouse-ops-bypass", from, to],
    queryFn: () => getBypassReport(from, to),
    enabled: periodOk,
  });

  const open = (base: Omit<BypassFilter, "dateFrom" | "dateTo">) =>
    q.data && setDrill({ ...base, dateFrom: q.data.date_from, dateTo: q.data.date_to });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Собранные в портале паллеты: как они ушли — через портал или мимо него
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-muted-foreground">
            Собраны с
            <DateInput
              value={from}
              onChange={(iso) => iso && setFrom(iso)}
              ariaLabel="Начало периода сборки"
              className="w-40"
            />
          </label>
          <label className="text-xs text-muted-foreground">
            По
            <DateInput
              value={to}
              onChange={(iso) => iso && setTo(iso)}
              ariaLabel="Конец периода сборки"
              className="w-40"
            />
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void q.refetch()}
            disabled={!periodOk}
            aria-label="Обновить"
          >
            <RefreshCw className="size-4" />
          </Button>
        </div>
      </div>
      {!periodOk && <p className="text-sm text-rose-700">Конец периода раньше начала.</p>}
      {periodOk && !q.data && (
        <LoadState isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()} />
      )}
      {periodOk && q.data && q.error && (
        <RefetchError error={q.error} onRetry={() => void q.refetch()} />
      )}
      {q.data && (
        <>
          <ul className="space-y-1 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
            {q.data.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
          <div
            className={cn(
              "rounded-lg border p-3 text-sm",
              q.data.posting_sync.ok
                ? "bg-white text-muted-foreground"
                : "border-rose-300 bg-rose-50 text-rose-800",
            )}
            role={q.data.posting_sync.ok ? undefined : "alert"}
          >
            {postingSyncText(q.data.posting_sync)}
            {!q.data.posting_sync.ok && (
              <div>«Мимо портала» и «без сигнала» могут быть неточны, пока сверка не пройдёт.</div>
            )}
          </div>

          <ExcludedBlock excluded={q.data.excluded} />

          <section className="rounded-lg border bg-white p-3" aria-label="По месяцам">
            <h2 className="mb-2 text-base font-semibold">По месяцам</h2>
            {q.data.months.length === 0 ? (
              <p className="text-sm text-muted-foreground">За период паллет не собирали.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <CountsHead first={["Месяц"]} />
                  <tbody>
                    {q.data.months.map((m) => (
                      <tr key={m.month} className="border-t align-top">
                        <td className="py-1.5 pr-2">{monthTitle(m.month)}</td>
                        <CountsCells
                          c={m}
                          titles={q.data.state_titles}
                          onOpen={(signal, what) =>
                            open({ month: m.month, signal, title: `${monthTitle(m.month)}: ${what}` })
                          }
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section
            className="rounded-lg border bg-white p-3"
            aria-label="По направлениям и покупателям"
          >
            <h2 className="mb-2 text-base font-semibold">По направлениям и покупателям</h2>
            {q.data.groups.length === 0 ? (
              <p className="text-sm text-muted-foreground">За период паллет не собирали.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] text-left text-sm">
                  <CountsHead first={["Месяц", "Направление", "Юрлицо → покупатель"]} />
                  <tbody>
                    {q.data.groups.map((g) => (
                      <tr key={groupKey(g)} className="border-t align-top">
                        <td className="whitespace-nowrap py-1.5 pr-2">{monthTitle(g.month)}</td>
                        <td className="py-1.5 pr-2">{g.direction_title}</td>
                        <td className="py-1.5 pr-2">
                          {g.organization_name || "—"} → {g.kontragent_name || "—"}
                        </td>
                        <CountsCells
                          c={g}
                          titles={q.data.state_titles}
                          onOpen={(signal, what) =>
                            open({
                              month: g.month,
                              direction: g.direction,
                              organization: g.organization_name,
                              kontragent: g.kontragent_name,
                              signal,
                              title:
                                `${monthTitle(g.month)}, ${g.organization_name || "—"} → ` +
                                `${g.kontragent_name || "—"}: ${what}`,
                            })
                          }
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
      {drill && <BypassPalletsSheet filter={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

function groupKey(g: BypassGroup): string {
  return [g.month, g.direction, g.organization_name, g.kontragent_name].join("|");
}

function CountsHead({ first }: { first: string[] }): React.ReactElement {
  return (
    <thead className="text-xs text-muted-foreground">
      <tr>
        {first.map((t) => (
          <th key={t} className="py-1 pr-2 font-medium">
            {t}
          </th>
        ))}
        <th className="py-1 pr-2 text-right font-medium">Собрано</th>
        <th className="py-1 pr-2 text-right font-medium">Через портал</th>
        <th className="py-1 pr-2 text-right font-medium">Через портал без скана паллеты</th>
        <th className="py-1 pr-2 text-right font-medium">Мимо портала</th>
        <th className="py-1 pr-2 text-right font-medium">Без сигнала</th>
        <th className="py-1 text-right font-medium">Доля мимо</th>
      </tr>
    </thead>
  );
}

function CountsCells({
  c,
  titles,
  onOpen,
}: {
  c: BypassCounts;
  titles: Record<string, string>;
  onOpen: (signal: BypassSignal | undefined, what: string) => void;
}): React.ReactElement {
  const cell = (n: number, signal: BypassSignal | undefined, what: string) => (
    <td className="py-1.5 pr-2 text-right">
      {n > 0 ? (
        <button
          type="button"
          className="text-primary underline-offset-2 hover:underline"
          onClick={() => onOpen(signal, what)}
          aria-label={`${what}: ${n}`}
        >
          {ruNum.format(n)}
        </button>
      ) : (
        "0"
      )}
    </td>
  );
  return (
    <>
      {cell(c.assembled, undefined, "собрано")}
      {cell(c.scan, "scan", "через портал")}
      {cell(c.portal_order, "portal_order", "через портал без скана паллеты")}
      <td className={cn("py-1.5 pr-2 text-right", c.posting > 0 && "text-amber-800")}>
        {c.posting > 0 ? (
          <button
            type="button"
            className="text-primary underline-offset-2 hover:underline"
            onClick={() => onOpen("posting", "мимо портала")}
            aria-label={`мимо портала: ${c.posting}`}
          >
            {ruNum.format(c.posting)}
          </button>
        ) : (
          "0"
        )}
        {c.doc_before_assembly > 0 && (
          <div className="text-xs text-muted-foreground">
            из них дата документа раньше сборки — {ruNum.format(c.doc_before_assembly)}
          </div>
        )}
      </td>
      <td className={cn("py-1.5 pr-2 text-right", c.none > 0 && "text-amber-800")}>
        {c.none > 0 ? (
          <button
            type="button"
            className="text-primary underline-offset-2 hover:underline"
            onClick={() => onOpen("none", "без сигнала")}
            aria-label={`без сигнала: ${c.none}`}
          >
            {ruNum.format(c.none)}
          </button>
        ) : (
          "0"
        )}
        {c.none > 0 && (
          <div className="text-xs text-muted-foreground">{noneStatesText(c, titles)}</div>
        )}
      </td>
      <td className="py-1.5 text-right font-medium">{bypassPctText(c)}</td>
    </>
  );
}

function BypassPalletsSheet({
  filter,
  onClose,
}: {
  filter: BypassFilter;
  onClose: () => void;
}): React.ReactElement {
  const q = useQuery({
    queryKey: ["warehouse-ops-bypass-pallets", filter],
    queryFn: () => getBypassPallets(filter),
  });
  return (
    <BottomSheet onClose={onClose} panelClassName="max-h-[85dvh] overflow-hidden flex flex-col">
      <div className="flex items-start justify-between gap-2 border-b p-3">
        <div>
          <div className="text-[15px] font-semibold">{filter.title}</div>
          <div className="text-xs text-muted-foreground">
            Собраны {fmtDate(filter.dateFrom)} — {fmtDate(filter.dateTo)}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Закрыть">
          <X className="size-4" />
        </Button>
      </div>
      <div className="space-y-3 overflow-auto p-3">
        {q.data && q.error && <RefetchError error={q.error} onRetry={() => void q.refetch()} />}
        {!q.data ? (
          <LoadState isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()} />
        ) : (
          <section aria-label="Паллеты">
            <div className="mb-1 text-xs font-semibold">Паллет · {q.data.pallets.length}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    {BYPASS_PALLET_COLUMNS.map((c) => (
                      <th key={c.key} className="py-1 pr-2 font-medium">
                        {c.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {q.data.pallets.map((p, i) => (
                    <tr key={i} className="border-t align-top">
                      {BYPASS_PALLET_COLUMNS.map((c) => (
                        <td key={c.key} className="py-1 pr-2">
                          {detailValue(c.key, p[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </BottomSheet>
  );
}
